// Row/entry types + pure helpers for the crew-schedule grid. Extracted
// verbatim from ManpowerPage.tsx; extended with the fabrication crew-phase
// split (CrewRow / buildCrewRows).

import { entryDateStr, isWeekendYmd } from './gridDates';
import { isFabDivision, type ProjectPhase, type PhaseKey } from '@/lib/schedule';
import { startMismatch } from '@/lib/startAlignment';

/** Crew phase on a schedule entry. Fabrication-division jobs run two crews per
 *  day ('fabrication' shop + 'installation' field); every other division's
 *  entries carry null. */
export type CrewPhase = 'fabrication' | 'installation' | null;

export interface DailyEntry {
  id: number;
  workDate: string;
  projectId: number;
  leaderId: number | null;
  phase?: CrewPhase;
  crewCount: number;
  isOverride?: boolean;
  notes: string | null;
  /** Present only on the draft overlay (edit mode): this cell has a staged,
   *  unpublished value; publishedCrewCount is what PMs currently see. */
  isDraft?: boolean;
  publishedCrewCount?: number;
}

export interface ProjectRow {
  projectId: number;
  jobNumber: string;
  title: string;
  isSystac: boolean;
  isClosedWonPlaceholder: boolean;
  isUnmatchedImport: boolean;
  isMaintenance?: boolean;
  parentProjectId?: number | null;
  isLoanedSpToGc?: boolean;
  /** #396 slice 3: a travel job — feeds the gap view's demand split. */
  travelJob?: boolean;
  loanFromDivision?: { id: number; label: string } | null;
  division: { id: number; label: string } | null;
  /** Primary PM's display name, merged in from project-options. Drives the PM
   *  column, the PM sort, and the "show only this PM's jobs" filter. Null when
   *  no PM is set on the project. */
  pm?: string | null;
  dailyEntries: DailyEntry[];
  /** Per-phase schedule (fab/install dates + completion % + budget hours) from
   *  project_phases, attached by GET /manpower/schedule. Absent for projects
   *  merged in client-side from project-options (no entries in the window). */
  phases?: ProjectPhase[];
  /**
   * YYYY-MM-DD (or full ISO) of the project's currentStartDate, attached
   * client-side by merging the manpower project-options response onto the schedule
   * rows. Used to flag projects that start in the visible window but have
   * no crew scheduled — see the synthetic "Needs Scheduling" section.
   */
  currentStartDate?: string | null;
  /**
   * YYYY-MM-DD of the first day this job has PUBLISHED crew booked — server-side
   * and window-independent, merged in from project-options alongside
   * currentStartDate (#385).
   *
   * ⚠️ Deliberately NOT derived from `dailyEntries`. Those carry only the visible
   * window, so a flag computed from them would appear and vanish as the
   * scheduler pages through weeks — a property of the viewport rather than of the
   * job. See lib/startAlignment.
   */
  firstCrewDate?: string | null;
  /**
   * Anticipated peak crew from the bid (`projects.bid_peak_crew`, imported from
   * Bid Tracker's AnticipatedManpowerSize). Merged in from project-options
   * alongside currentStartDate. Drives the SYSTAC forecast — see systacForecast.
   */
  bidPeakCrew?: number | null;
  /** Anticipated duration in CALENDAR days (`AnticipatedDuration`). */
  bidDurationDays?: number | null;
  /**
   * The project's original start date, used only as a fallback for the
   * forecast when currentStartDate has been cleared. Kept separate from
   * currentStartDate because other consumers key off that field — the "Needs
   * Scheduling" rule in particular — and coalescing there would quietly widen
   * that section.
   */
  originalStartDate?: string | null;
}

/** A bid-derived manpower forecast for a pursuit, in whole calendar days. */
export interface SystacForecast {
  /** First forecast day, YYYY-MM-DD. */
  fromYmd: string;
  /** Last forecast day inclusive, YYYY-MM-DD. */
  toYmd: string;
  /** Flat anticipated crew for every day in the range. */
  crew: number;
}

/**
 * The manpower forecast a SYSTAC pursuit carries from its bid (#330).
 *
 * Estimators already state crew size and duration in Bid Tracker when a job is
 * moved to SYSTAC, and PMT imports both — so the schedule can SHOW the forecast
 * without anyone re-keying it. Nothing is written: this derives a read-only
 * overlay from fields the project already has.
 *
 * ⚠️ SYSTAC ONLY. A real job's crew comes from the schedule; showing a stale
 * bid figure beside actual entries would invite reading it as the plan.
 *
 * ⚠️ FLAT, AND HONEST ABOUT IT. The bid gives a PEAK crew and a duration, not a
 * curve — real jobs ramp up and down. Spreading the peak evenly overstates
 * total man-days, so this is labelled a peak forecast wherever it renders
 * rather than presented as a schedule. Inventing a ramp would be fabricating
 * detail the bid never contained.
 *
 * ⚠️ The RUN is in CALENDAR days. `AnticipatedDuration` is stored as a raw
 * integer via parseInt2 with no working-day interpretation anywhere, so
 * treating it as calendar days is the only reading the data supports. What the
 * run does NOT imply is weekend working — forecastCrewOn claims no crew on a
 * Saturday or Sunday inside the span. The bid says how long, never which days.
 *
 * Returns null unless all three inputs are present and positive — a pursuit
 * missing any of them simply has no forecast, which is the honest outcome.
 */
export function systacForecast(project: ProjectRow): SystacForecast | null {
  if (!project.isSystac) return null;
  const crew = project.bidPeakCrew ?? 0;
  const days = project.bidDurationDays ?? 0;
  if (!(crew > 0) || !(days > 0)) return null;
  // Falls back to the original start, mirroring the duration, which already
  // coalesces current onto original. Without it a pursuit whose current start
  // had been cleared lost an otherwise perfectly good forecast.
  const startYmd = (project.currentStartDate ?? project.originalStartDate ?? '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(startYmd)) return null;
  return { fromYmd: startYmd, toYmd: addYmd(startYmd, days - 1), crew };
}

/**
 * Which crew line carries the project-level bid forecast.
 *
 * ⚠️ A FAB JOB HAS TWO CREW LINES for one project — buildCrewRows splits it
 * into fabrication and installation — but the bid gives ONE whole-job crew
 * figure. Rendering it per line showed it twice on an empty fab pursuit, and
 * left the full peak on the second line when the first had real crew, which
 * both overstates the number and breaks the rule that scheduled work wins.
 *
 * So it lands on the first line only: `fabrication` for a fab job (the order
 * buildCrewRows returns), and the single unphased line for everything else.
 * The tooltip says it is the whole-job figure, because on a fab job it sits
 * beside the shop line while covering both.
 */
export function isForecastRow(row: CrewRow): boolean {
  return row.phase === null || row.phase === 'fabrication';
}

/**
 * Forecast crew on one date, or null when the bid does not speak to that day.
 *
 * ⚠️ WEEKENDS ARE NOT FORECAST. The bid gives a duration in CALENDAR days, but
 * it says nothing about which days get worked, and this figure now counts
 * toward the daily totals and Headroom. Spreading it across Saturdays and
 * Sundays would assert weekend working that nobody has planned and that the
 * board otherwise treats as the exception (product owner, 2026-08-31: "I don't want to
 * assume we will work on weekends"). The run still SPANS those days — it simply
 * claims no crew on them.
 *
 * Booked crew is untouched by this: a scheduler who genuinely books a Saturday
 * gets a Saturday, because that is stated rather than assumed.
 */
export function forecastCrewOn(fc: SystacForecast | null, ds: string): number | null {
  if (!fc) return null;
  if (isWeekendYmd(ds)) return null;
  return ds >= fc.fromYmd && ds <= fc.toYmd ? fc.crew : null;
}

/** Add whole days to a YYYY-MM-DD, staying on the calendar (no timezone drift). */
function addYmd(ymd: string, days: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + days);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${p(dt.getMonth() + 1)}-${p(dt.getDate())}`;
}

export interface Section {
  key: string;
  label: string;
  isClosedWonPlaceholder: boolean;
  divisionId: number | null;
  /** True for the synthetic "Needs Scheduling" section. */
  isNeedsScheduling?: boolean;
  projects: ProjectRow[];
}

// Surfaces survey_requests in Scheduled or In Progress status on the manpower
// grid so PMs and foremen can see when survey plans to be — or currently is —
// on site.
export interface ScheduledSurveyRequest {
  id: number;
  projectId: number | null;
  jobName: string | null;
  scheduledDateTime: string | null;
  scheduledTechnicianText: string | null;
  duration: string | null;
  status: string;
  requestedByText: string | null;
  requestedBy: { id: string; displayName: string | null } | null;
  project: { id: number; jobNumber: string; title: string; primaryPm: { displayName: string | null } | null } | null;
}

/** Normalize project names for case/whitespace/punctuation-insensitive comparison. */
export function normalizeName(s: string): string {
  return s.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').replace(/\s+/g, ' ').trim();
}

/** SYSTAC jobs are identified by their job-number prefix. We deliberately key
 *  SYSTAC status off the prefix rather than the server-side is_systac flag: that
 *  flag is sticky, so a SYSTAC job that later gets awarded and assigned a real
 *  job number keeps is_systac=1 and would wrongly keep badging/grouping/filtering
 *  as SYSTAC. The current job number is the source of truth. */
export function isSystacJobNumber(jobNumber: string): boolean {
  return /^SYSTAC-/i.test(jobNumber.trim());
}

/** ClosedWon bid placeholders, same story as SYSTAC above: the server-side
 *  is_closed_won_placeholder flag is sticky, so an awarded job that's been
 *  renumbered to a real Sage job # would stay stuck in the ClosedWon section
 *  forever. The current job number is the source of truth — renumbering IS
 *  the award action (the Projects edit drawer's renumber banner keys off the
 *  same prefix). */
export function isClosedWonJobNumber(jobNumber: string): boolean {
  return /^ClosedWon-/i.test(jobNumber.trim());
}

/** An Excel-import stub job number (IMP-, from the manpower import's
 *  syntheticJobNumber) — a project auto-created without a matching Sage job
 *  number. This is what the "Needs Job#" badge flags. SYSTAC-/ClosedWon- are
 *  bid placeholders that carry their own meaningful numbers (they are not
 *  "missing" a job #), and MNT- are maintenance sub-projects — none of those
 *  should read as "Needs Job#". Once a real number is assigned the IMP- prefix
 *  is gone, so the flag drops. */
export function isImportStubJobNumber(jobNumber: string): boolean {
  return /^IMP-/i.test(jobNumber.trim());
}

/** Reorder a flat row list so each maintenance sub-project follows its parent.
 *  Top-level rows keep their incoming order; children are slotted in right
 *  after their parent; orphans (parent absent from this list) go to the end. */
export function nestSubProjects<T extends { projectId: number; parentProjectId?: number | null }>(rows: T[]): T[] {
  const childrenByParent = new Map<number, T[]>();
  const tops: T[] = [];
  for (const r of rows) {
    if (r.parentProjectId != null) {
      const arr = childrenByParent.get(r.parentProjectId) ?? [];
      arr.push(r);
      childrenByParent.set(r.parentProjectId, arr);
    } else {
      tops.push(r);
    }
  }
  const out: T[] = [];
  const placed = new Set<number>();
  for (const t of tops) {
    out.push(t); placed.add(t.projectId);
    for (const kid of childrenByParent.get(t.projectId) ?? []) { out.push(kid); placed.add(kid.projectId); }
  }
  for (const r of rows) if (!placed.has(r.projectId)) out.push(r); // orphans
  return out;
}

/** Crew count for a project on a given YYYY-MM-DD — the SUM across phases.
 *  A fabrication job carries up to two rows per day (fabrication +
 *  installation), so totals must add them, not take the first match. */
export function getCrewCount(project: ProjectRow, ds: string): number {
  let sum = 0;
  for (const e of project.dailyEntries) {
    if (entryDateStr(e.workDate) === ds) sum += e.crewCount;
  }
  return sum;
}

/**
 * Crew a project needs on a day, **bid forecast included** (#425).
 *
 * This is the number the grid draws and the number the totals add, so the two
 * cannot drift. Booked crew always wins; a SYSTAC pursuit with nothing booked
 * that day falls back to its bid forecast. Every other job gets its booked
 * crew and nothing more, because `systacForecast` returns null off a SYSTAC
 * job.
 *
 * ⚠️ Deliberately NOT folded into `getCrewCount`. That function answers "what
 * is actually booked", and the forecast's own fallback is defined in terms of
 * it — teaching it about the forecast would make that circular, and would also
 * corrupt the draft-versus-published comparisons that ask the same question.
 *
 * Product owner, 2026-08-31: a SYSTAC figure is a real claim on capacity, so it counts
 * like any other, and the SYSTAC toggle is what separates committed from
 * speculative — switch it off and the pursuits leave the totals along with the
 * rows. That replaced the original treatment, where the forecast rendered in
 * muted italic and no total could see it, so the toggle moved the number by
 * nothing at all for exactly the jobs it exists to model (operations,
 * feedback #64 → #425).
 */
export function getPlannedCrewCount(project: ProjectRow, ds: string): number {
  const booked = getCrewCount(project, ds);
  if (booked > 0) return booked;
  return forecastCrewOn(systacForecast(project), ds) ?? 0;
}

/**
 * Whether this row is currently DISPLAYING bid crew rather than booked crew.
 *
 * Needed because the two facts a pursuit row shows now look identical: since
 * #425 a forecast paints like a booking, so "⚠ Needs Scheduling" sat above a
 * row full of solid numbers and read as a contradiction. The badge uses this to
 * say which kind of crew is on screen instead of the generic wording.
 *
 * Judged on the VISIBLE window and per day, matching what the cells actually
 * draw: a forecast day only shows when the project has nothing booked that day,
 * and weekends are not forecast at all. A pursuit whose whole run is booked, or
 * whose run lands entirely on a weekend, is displaying no bid crew and gets the
 * ordinary wording.
 */
export function showsForecastCrew(project: ProjectRow, dates: readonly string[]): boolean {
  const fc = systacForecast(project);
  if (!fc) return false;
  return dates.some((d) => getCrewCount(project, d) === 0 && forecastCrewOn(fc, d) != null);
}

/** Which divisions the crew grid shows. `sp` excludes fabrication, `fab` keeps
 *  only fabrication, `all` keeps everything. */
export type ScheduleScope = 'sp' | 'all' | 'fab';

/** Resolve the grid's scope from `?scope=` and the viewer's role.
 *
 *  Self-Perform is the default for most people: fabrication crews are shop
 *  labor and aren't available for other self-perform work, so counting them in
 *  the headline number overstated the crew a scheduler could deploy (#260).
 *
 *  A fab-scoped editor defaults to the Fabrication lens instead. Self-Perform
 *  would open their page on a grid holding none of the jobs they may edit —
 *  the one view that is useless to them. An explicit param always wins, for
 *  everyone, so a shared link resolves the same way whoever opens it. */
export function resolveScope(param: string | null, fabEditOnly: boolean): ScheduleScope {
  if (param === 'fab' || param === 'all' || param === 'sp') return param;
  return fabEditOnly ? 'fab' : 'sp';
}

/** Everything that can be hiding rows from the crew grid right now. */
export interface GridNarrowings {
  search: string;
  pmFilter: string;
  travelFilter: 'all' | 'travel' | 'local';
  scope: ScheduleScope;
  /** Labels of the division chips switched off. */
  hiddenSectionLabels: readonly string[];
  systacHidden: boolean;
  /** The "Start ≠ Crew" chip, which ISOLATES rather than hides (#385). */
  mismatchOnly: boolean;
}

/**
 * The active narrowings, each named the way its own control names it, for an
 * empty grid to say what is actually hiding the rows.
 *
 * ⚠️ THE EMPTY GRID USED TO GUESS, and it guessed wrong in four of the five
 * ways the grid can empty. It was a two-way branch on whether any section
 * survived: none meant "No schedule entries found for this period", otherwise
 * "All sections are hidden — use the chips above to show one". But the chips
 * are only one of six things that empties it, so a job search matching nothing,
 * a Type filter with no jobs of that type, and a scope holding back the only
 * division with work all sent the reader to chips that were not the problem and
 * might not be able to fix it — while a PM filter matching nobody claimed the
 * whole period had no work in it at all.
 *
 * Codex raised the Type case on #498. It is the same defect as the other three,
 * so this names every narrowing rather than special-casing the newest one.
 *
 * ⚠️ SCOPE IS LISTED EVEN AT ITS DEFAULT. Self-Perform is what most people open
 * the page on and it hides every Fabrication job, so in a week where fabrication
 * is the only work the grid is empty entirely because of a control the reader
 * never touched. That is the case most worth naming, not the one to omit for
 * being ordinary.
 *
 * Returns them in the order the controls appear above the grid, so the sentence
 * reads in the same direction the eye scans.
 */
export function describeNarrowings(n: GridNarrowings): string[] {
  const out: string[] = [];
  const search = n.search.trim();
  if (search) out.push(`the job search “${search}”`);
  if (n.pmFilter) out.push(`the PM filter (${n.pmFilter})`);
  if (n.travelFilter !== 'all') {
    out.push(`the ${n.travelFilter === 'travel' ? 'Travel' : 'Local'} type filter`);
  }
  if (n.scope !== 'all') out.push(`the ${n.scope === 'fab' ? 'Fabrication' : 'Self-Perform'} view`);
  if (n.hiddenSectionLabels.length > 0) {
    out.push(`hidden divisions (${n.hiddenSectionLabels.join(', ')})`);
  }
  if (n.systacHidden) out.push('the SYSTAC chip');
  // Last because its chip sits last, and because it is the only one that can
  // empty the grid by ISOLATING: with it on, a portfolio where nothing
  // disagrees is correctly empty, and the reader needs to be told that is what
  // happened rather than left looking for hidden divisions.
  if (n.mismatchOnly) out.push('the Start ≠ Crew filter');
  return out;
}

/** What one row contributes to a subtotal or grand total.
 *
 *  A maintenance parent is a header row: its cells render the roll-up of its
 *  sub-projects, and each of those subs is a row of its own. Counting the
 *  parent as well doubled the job in the totals (#296) — and worse, counted
 *  crew nobody could see: flagging a project maintenance leaves any crew
 *  already booked directly on it in `mp_schedule_entries`, where the parent's
 *  cells no longer render it but the totals still added it.
 *
 *  The rule is that a total reads off the same basis the grid draws. Normally
 *  every sub is on the grid and speaks for itself, so the parent contributes
 *  nothing. But a filter can keep a parent while dropping its subs — search by
 *  the parent's job number, or the Fabrication lens — and the parent's cells
 *  still paint the full roll-up. So the parent speaks for exactly the subs that
 *  are NOT on the grid; otherwise a filtered view showed crew in the parent's
 *  cells and 0 in the total beneath it.
 *
 *  @param offGridChildren the parent's sub-projects that are not themselves
 *    rows in the current view. Ignored for non-maintenance rows. */
export function getTotalledCrewCount(
  project: ProjectRow,
  ds: string,
  offGridChildren: readonly ProjectRow[] = [],
): number {
  if (!project.isMaintenance) return getPlannedCrewCount(project, ds);
  // A sub-project inherits nothing from its parent's bid, but going through
  // the same helper keeps one definition of "what this row contributes" — and
  // a sub that is itself a SYSTAC pursuit then counts like any other row.
  return offGridChildren.reduce((sum, child) => sum + getPlannedCrewCount(child, ds), 0);
}

// ─── Fabrication crew-phase split ────────────────────────────────────────────

/** One renderable crew line. A fabrication-division job emits two (shop
 *  fabrication + field installation); everything else emits one (phase null,
 *  the classic "Crew" row). */
export interface CrewRow {
  /** `${projectId}:${phase ?? 'crew'}` — stable render key. */
  key: string;
  projectId: number;
  phase: CrewPhase;
  project: ProjectRow;
  /** The phase's schedule span — actual, else the current plan, else the
   *  baseline (#380) — when known. Drives the stepper's fill range (edit mode)
   *  and span shading. */
  phaseSpan: { start: string; end: string } | null;
}

/** Whether a schedule row belongs to the Fabrication division. */
export function isFabProject(project: Pick<ProjectRow, 'division'>): boolean {
  return isFabDivision({ division: project.division ?? undefined });
}

/**
 * The phase's schedule window for the grid.
 *
 * ⚠️ `actual → planned → original` on the start, the same precedence as
 * `effectiveStart` and `listPhaseSpans` (#380), and it has to be. A PM re-planning
 * a phase that has not started yet moves `plannedStart` and nothing else, so
 * reading only `actualStart ?? originalStart` would leave this grid shading the
 * old window, the bulk stepper filling the old days, and crew setup defaulting to
 * the old dates — the Manpower view and the Schedule & Dates card disagreeing
 * about when the job runs.
 *
 * Not calling `effectiveStart` directly only because it also returns the
 * provenance, which nothing here needs; keep the order in step with it.
 */
function spanFor(project: ProjectRow, phase: PhaseKey): { start: string; end: string } | null {
  const ph = project.phases?.find((p) => p.phase === phase);
  if (!ph) return null;
  const start = ph.actualStart ?? ph.plannedStart ?? ph.originalStart;
  const end = ph.actualEnd ?? ph.originalEnd;
  return start && end ? { start: String(start).slice(0, 10), end: String(end).slice(0, 10) } : null;
}

/** The crew lines a project renders on the schedule grid. */
export function buildCrewRows(project: ProjectRow): CrewRow[] {
  // Maintenance parents stay single header rows; sub-projects stay single crew
  // rows — the fab split applies to real fab-division jobs only.
  if (isFabProject(project) && !project.isMaintenance && project.parentProjectId == null) {
    return [
      { key: `${project.projectId}:fabrication`,  projectId: project.projectId, phase: 'fabrication',  project, phaseSpan: spanFor(project, 'fabrication') },
      { key: `${project.projectId}:installation`, projectId: project.projectId, phase: 'installation', project, phaseSpan: spanFor(project, 'installation') },
    ];
  }
  return [{ key: `${project.projectId}:crew`, projectId: project.projectId, phase: null, project, phaseSpan: null }];
}

/** Crew count for one crew line on a date. On a fab job, legacy/imported
 *  NULL-phase entries render on the FABRICATION line (display fallback) so
 *  nothing disappears before it's re-phased. */
export function getPhaseCrewCount(row: CrewRow, ds: string): number {
  const fab = row.phase != null; // fab-split row
  let sum = 0;
  for (const e of row.project.dailyEntries) {
    if (entryDateStr(e.workDate) !== ds) continue;
    const ePhase = e.phase ?? null;
    if (!fab) {
      sum += e.crewCount;
    } else if (ePhase === row.phase || (row.phase === 'fabrication' && ePhase === null)) {
      sum += e.crewCount;
    }
  }
  return sum;
}

/** The NULL-phase entry for a date on a fab job (a legacy/imported row shown
 *  via the fabrication-line fallback). Editing the fabrication line migrates
 *  it: the new value writes phase='fabrication' and this row is zeroed.
 *
 *  ⚠️ "Still needs zeroing" means a live legacy count, whatever the overlay
 *  shows. In edit mode another scheduler's draft can already have staged the
 *  zero (crewCount 0, publishedCrewCount > 0). That zero is THEIRS: outside
 *  this scheduler's Publish and undone by their Reset, after which the
 *  published legacy count reappears and is summed with the new phased one.
 *  So a staged zero over a published count still counts, and every caller
 *  (typing, the − / + stepper, multi-cell edits) re-stages the zero in its own
 *  batch (Codex on #612). */
export function nullPhaseEntryFor(project: ProjectRow, ds: string): DailyEntry | undefined {
  return project.dailyEntries.find((e) =>
    entryDateStr(e.workDate) === ds && (e.phase ?? null) === null
    && (e.crewCount > 0 || (e.isDraft === true && (e.publishedCrewCount ?? 0) > 0)));
}

/** Whether an entry belongs to a crew line (same matching as getPhaseCrewCount). */
function entryMatchesRow(row: CrewRow, e: DailyEntry): boolean {
  const ePhase = e.phase ?? null;
  if (row.phase == null) return true;
  return ePhase === row.phase || (row.phase === 'fabrication' && ePhase === null);
}

/** Cell readout for one crew line + date: summed count plus draft/override
 *  markers (any matching entry sets the flag). */
export function getPhaseCellMeta(row: CrewRow, ds: string): { count: number; isDraft: boolean; isOverride: boolean } {
  let count = 0; let isDraft = false; let isOverride = false;
  for (const e of row.project.dailyEntries) {
    if (entryDateStr(e.workDate) !== ds || !entryMatchesRow(row, e)) continue;
    count += e.crewCount;
    if (e.isDraft) isDraft = true;
    if (e.isOverride) isOverride = true;
  }
  return { count, isDraft, isOverride };
}

const dowOf = (ds: string) => new Date(ds + 'T12:00:00').getDay();
const MON_FRI = new Set([1, 2, 3, 4, 5]);

/**
 * Which days of the week this crew row ACTUALLY works, inferred from the days
 * that already carry crew.
 *
 * The stepper used to assume Mon–Fri. Once bulk setup could lay in 4, 5, 6 and
 * 7-day weeks (PM request, 2026-07-27) that assumption broke: on a six-day job
 * the stepper would reshape Mon–Fri and silently leave every Saturday at its old
 * headcount, so the row ended up with two different crew sizes. Reading the
 * pattern off the row keeps the two features consistent.
 *
 * Falls back to Mon–Fri for an empty row, which is the sane default for a job
 * nobody has scheduled yet.
 */
function workingDows(row: CrewRow, windowDays: string[]): Set<number> {
  const found = new Set<number>();
  for (const ds of windowDays) {
    if (getPhaseCrewCount(row, ds) > 0) found.add(dowOf(ds));
  }
  return found.size > 0 ? found : MON_FRI;
}

/** The days a crew stepper writes: the row's own working days within the window
 *  (inferred from where it already has crew — see workingDows), narrowed to the
 *  phase span when known, else to already-populated days (if any) so a plain
 *  Crew row's stepper reshapes its existing run instead of flooding the
 *  window. Day-level overrides are skipped — they win over the base rate. */
export function stepperTargetDays(row: CrewRow, windowDays: string[]): string[] {
  const dows = workingDows(row, windowDays);
  const workDays = windowDays.filter((ds) => dows.has(dowOf(ds)));
  const inSpan = row.phaseSpan
    ? workDays.filter((ds) => ds >= row.phaseSpan!.start && ds <= row.phaseSpan!.end)
    : [];
  let base = inSpan;
  if (base.length === 0) {
    const populated = workDays.filter((ds) => getPhaseCrewCount(row, ds) > 0);
    base = populated.length > 0 ? populated : workDays;
  }
  return base.filter((ds) => !getPhaseCellMeta(row, ds).isOverride);
}

/** A crew line's "base rate" from a set of day counts: the most common nonzero
 *  count, ties to the larger, 0 when none. The one definition — the stepper
 *  and the multi-cell pin rule (bulkCellWrites, #589) both read it, so they
 *  cannot disagree about what a line's normal rate is. */
export function baseRate(counts: readonly number[]): number {
  const freq = new Map<number, number>();
  for (const c of counts) if (c > 0) freq.set(c, (freq.get(c) ?? 0) + 1);
  let best = 0; let bestN = 0;
  for (const [v, n] of freq) if (n > bestN || (n === bestN && v > best)) { best = v; bestN = n; }
  return best;
}

/** The stepper's displayed value: the base rate across the target days. */
export function stepperValue(row: CrewRow, targetDays: string[]): number {
  return baseRate(targetDays.map((ds) => getPhaseCrewCount(row, ds)));
}

/**
 * The badges a row carries, as text for the Excel export.
 *
 * ⚠️ THIS COLUMN *IS* THE ROW'S BADGES. A reader of the workbook has no tooltip
 * to hover and no colour to interpret — this string is their only way to tell a
 * flagged job from a clean one. So every badge the grid paints has to appear
 * here, and a badge added to the grid without being added here is invisible in
 * the file while being obvious on screen.
 *
 * That has now happened three times on one change: a new signal wired into some
 * of the places that need it and not others (the cache invalidations, the
 * workbook's FILTERED disclosure, and then this). Extracted out of the export
 * memo so the set is testable in one place rather than asserted by reading.
 *
 * ⚠️ The mismatch flag carries BOTH DATES, not just the word. The grid puts them
 * in a tooltip; a spreadsheet has nowhere to put them but the cell, and "Start ≠
 * Crew" on its own tells a recipient that something is wrong without telling
 * them what — which is the least actionable form of a warning.
 */
export function exportRowFlags(
  p: ProjectRow,
  isNeedsScheduling: boolean,
  dateStrs: string[],
  fmtDate: (iso: string | null | undefined) => string,
): string[] {
  const flags: string[] = [];
  if (p.isMaintenance) flags.push('Maintenance');
  if (p.isLoanedSpToGc) flags.push(`SP Loan${p.loanFromDivision ? ` ← ${p.loanFromDivision.label}` : ''}`);
  if (p.isSystac) flags.push('SYSTAC');
  if (p.isUnmatchedImport && !p.isMaintenance && isImportStubJobNumber(p.jobNumber)) flags.push('Needs Job#');
  // Same wording the badge carries: on a pursuit the crew shown is anticipated
  // from the bid, not booked, and the file has to say which kind of number it is
  // holding just as the grid does.
  if (isNeedsScheduling) {
    flags.push(showsForecastCrew(p, dateStrs) ? 'Bid crew — not booked' : 'Needs scheduling');
  }
  const m = startMismatch(p.currentStartDate, p.firstCrewDate);
  if (m) flags.push(`Start ≠ Crew (planned ${fmtDate(m.plannedStart)}, crew from ${fmtDate(m.firstCrewDate)})`);
  return flags;
}
